'use client';

import type { components } from '@connected/api-client/api.generated';

import { portaalQueryOptions } from '@connected/portaal-sdk/queries/options';
import { Button } from '@connected/ui/components/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuShortcut,
    DropdownMenuTrigger,
} from '@connected/ui/components/dropdown-menu';
import { useQuery } from '@tanstack/react-query';
import {
    addDays,
    addMonths,
    addWeeks,
    format,
    startOfMonth,
    startOfWeek,
    subMonths,
    subWeeks,
} from 'date-fns';
import {
    ChevronDownIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
    CalendarEvent,
    CalendarView,
} from '@/components/event-calendar';

import {
    AgendaDaysToShow,
    AgendaView,
    CalendarDndProvider,
    DayView,
    EventGap,
    EventHeight,
    MonthView,
    WeekCellsHeight,
    WeekView,
} from '@/components/event-calendar';
import { cn } from '@/lib/utils';

// const MULTI_INSPECTOR_GRAY_PALETTE = ['#dcdcdc', '#c0c0c0', '#f5f5f5', '#F0F0F0', 'EDEDED'] as const;
const MULTI_INSPECTOR_GRAY_PALETTE = ['#dcdcdc'] as const;
const DEFAULT_PROJECT_GRAY = '#dcdcdc';

type ProjectPlanningQuery = components['schemas']['ListElectricalProjectPlanningQueryResultProject'];

export type EventCalendarProps = {
    organizationId?: string;
    events?: CalendarEvent[];
    onEventAdd?: (event: CalendarEvent) => void;
    onEventUpdate?: (event: CalendarEvent) => void;
    onEventDelete?: (eventId: string) => void;
    className?: string;
    initialView?: CalendarView;
};
export function EventCalendar({
    organizationId,
    events: externalEvents,
    onEventUpdate,
    className,
    initialView = 'month',
}: EventCalendarProps) {
    const [currentDate, setCurrentDate] = useState(() => new Date());
    const [view, setView] = useState<CalendarView>(initialView);
    const [isEventDialogOpen, setIsEventDialogOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
    const { t } = useTranslation('dashboard');

    const startDate = useMemo(() => {
        return view === 'month'
            ? startOfMonth(currentDate)
            : startOfWeek(currentDate, { weekStartsOn: 1 });
    }, [currentDate, view]);

    const projectsQuery = useQuery({
        ...portaalQueryOptions.electrical.planning.planningList({
            organizationId: organizationId ?? '',
            periodKind: view === 'month' ? 'OneMonth' : 'OneWeek',
            startDate: format(startDate, 'yyyy-MM-dd\'T\'HH:mm:ss.SSSXXX'),
        }),

        enabled: !!organizationId,
    });

    const projects: CalendarEvent[] = useMemo(() => {
        if (!projectsQuery.data?.Projects)
            return [];

        const statusLabels: Record<string, string> = {
            PriceQuote: t('dashboard.projects.table.columns.status.pricequote'),
            Request: t('dashboard.projects.table.columns.status.request'),
            Planned: t('dashboard.projects.table.columns.status.planned'),
            Inspection: t('dashboard.projects.table.columns.status.inpection'),
            RepeatInspection: t('dashboard.projects.table.columns.status.reinspection'),
            Complete: t('dashboard.projects.table.columns.status.complete'),
            Failed: t('dashboard.projects.table.columns.status.failed'),
        };

        const parseDateInfo = (isoDate?: string | null) => {
            if (!isoDate)
                return { date: null, hasTime: false } as const;

            const date = new Date(isoDate);
            const timeMatch = isoDate.match(/T(\d{2}):(\d{2})/);
            const hours = timeMatch ? Number(timeMatch[1]) : 0;
            const minutes = timeMatch ? Number(timeMatch[2]) : 0;
            const hasTime = hours !== 0 || minutes !== 0;

            return { date, hasTime } as const;
        };

        return projectsQuery.data.Projects.map((project) => {
            const uniqueUsers = project.ProjectUsers?.filter((user, index, self) =>
                index === self.findIndex(t => (
                    t.UserId === user.UserId
                )),
            ) ?? [];

            const hasMultipleInspectors = uniqueUsers.length > 1;
            let eventColor: string;

            if (hasMultipleInspectors) {
                eventColor = DEFAULT_PROJECT_GRAY;
            }
            else if (uniqueUsers.length === 1 && uniqueUsers[0].ColorCode) {
                eventColor = uniqueUsers[0].ColorCode;
            }
            else {
                eventColor = DEFAULT_PROJECT_GRAY;
            }

            function FilterProjectNames(project: ProjectPlanningQuery) {
                if (project && project?.Reports && project?.Reports?.length > 1) {
                    if (project?.Reports?.every(x => x.InspectionKind?.toString().toLocaleLowerCase().includes('scope'))) {
                        const templates = project.Reports.sort((a, b) => {
                            const an = a.InspectionKind?.match(/\d+/)?.[0];
                            const bn = b.InspectionKind?.match(/\d+/)?.[0];

                            return an && bn ? Number(an) - Number(bn) : an ? -1 : bn ? 1 : (a.InspectionKind ?? '').localeCompare(b.InspectionKind ?? '');
                        }).map(template => ({
                            ...template,
                            InspectionKind: template?.InspectionKind?.replace(/\D+/g, ''),
                        }));
                        return `Scope ${templates?.map(x => x.InspectionKind).join('-')}`;
                    }
                    return `${project?.Reports?.map(x => x.InspectionKind).join('-')}`;
                }
                if (project?.Reports?.length === 1) {
                    return `${project?.Reports?.[0]?.InspectionKind?.replace(/([A-Z])(?=\d)/gi, '$1 ') ?? '-'}`;
                }
                return '-';
            }

            const isMultiInspectorGrayColor = MULTI_INSPECTOR_GRAY_PALETTE.includes(eventColor as typeof MULTI_INSPECTOR_GRAY_PALETTE[number]);

            const address = project.ProjectObject?.Street && project.ProjectObject?.StreetNumber
                ? `${project.ProjectObject.Street} ${project.ProjectObject.StreetNumber}`
                : project.ProjectObject?.Name || 'Geen locatiegegevens';

            const inspectionKind = FilterProjectNames(project);
            const projectName = project.ProjectObject?.Name;

            const { date: projectStartDate, hasTime: startHasTime } = parseDateInfo(project.StartDate);
            const { date: projectEndDate } = parseDateInfo(project.EndDate);
            const startDate = projectStartDate ?? projectEndDate ?? new Date();
            const endDate = projectEndDate ?? startDate ?? new Date();
            const isAllDayEvent = !startHasTime;

            return {
                id: project.ElectricalProjectVersionId ?? '',
                title: address,
                description: project.Status ? statusLabels[project.Status] ?? project.Status : '',
                start: startDate,
                end: endDate,
                allDay: isAllDayEvent,
                color: eventColor,
                location: project.ProjectObject ?? undefined,
                inspectors: uniqueUsers,
                inspectionKind: inspectionKind ?? undefined,
                projectName: projectName ?? undefined,
                useDarkText: isMultiInspectorGrayColor || undefined,
            };
        });
    }, [projectsQuery.data, t]);

    const events = organizationId ? projects : (externalEvents ?? []);
    // Add keyboard shortcuts for view switching
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            setSelectedEvent(selectedEvent);
            // Skip if user is typing in an input, textarea or contentEditable element
            // or if the event dialog is open
            if (
                isEventDialogOpen
                || e.target instanceof HTMLInputElement
                || e.target instanceof HTMLTextAreaElement
                || (e.target instanceof HTMLElement && e.target.isContentEditable)
            ) {
                return;
            }

            switch (e.key.toLowerCase()) {
                case 'm':
                    setView('month');
                    break;
                case 'w':
                    setView('week');
                    break;
                case 'd':
                    setView('day');
                    break;
                case 'a':
                    setView('agenda');
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isEventDialogOpen, selectedEvent]);

    const handlePrevious = () => {
        if (view === 'month') {
            setCurrentDate(subMonths(currentDate, 1));
        }
        else if (view === 'week') {
            setCurrentDate(subWeeks(currentDate, 1));
        }
        else if (view === 'day') {
            setCurrentDate(addDays(currentDate, -1));
        }
        else if (view === 'agenda') {
            // For agenda view, go back 30 days (a full month)
            setCurrentDate(addDays(currentDate, -AgendaDaysToShow));
        }
    };

    const handleNext = () => {
        if (view === 'month') {
            setCurrentDate(addMonths(currentDate, 1));
        }
        else if (view === 'week') {
            setCurrentDate(addWeeks(currentDate, 1));
        }
        else if (view === 'day') {
            setCurrentDate(addDays(currentDate, 1));
        }
        else if (view === 'agenda') {
            // For agenda view, go forward 30 days (a full month)
            setCurrentDate(addDays(currentDate, AgendaDaysToShow));
        }
    };

    const handleEventSelect = (event: CalendarEvent) => {
        setSelectedEvent(event);
        setIsEventDialogOpen(true);
    };

    const handleEventCreate = () => {

    };

    const handleEventUpdate = (updatedEvent: CalendarEvent) => {
        onEventUpdate?.(updatedEvent);
    };

    const handleBackgroundClick = () => {
        setSelectedEvent(null);
    };

    const oneJanuary = new Date(currentDate.getFullYear(), 0, 1);
    const currentWeek = Math.ceil((((currentDate.getTime() - oneJanuary.getTime()) / 86400000) + oneJanuary.getDay() + 1) / 7);
    const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();

    return (
        <div
            className="flex flex-col rounded-lg border has-data-[slot=month-view]:flex-1"
            style={
                {
                    '--event-height': `${EventHeight}px`,
                    '--event-gap': `${EventGap}px`,
                    '--week-cells-height': `${WeekCellsHeight}px`,
                } as React.CSSProperties
            }
        >
            <CalendarDndProvider onEventUpdate={handleEventUpdate}>
                <div
                    className={cn(
                        'flex items-center justify-between p-2 sm:p-4',
                        className,
                    )}
                >
                    <div className="flex flex-col md:flex-row items-start md:items-center">
                        <div className="flex flex-row p-3 gap-3">
                            <div
                                className="w-16 h-14 rotate-0 opacity-100 rounded-md border flex text-center flex-col bg-[#FFFFFF]"
                            >
                                <div className="bg-[#FAFAFA] font-semibold text-quaternary">
                                    {t(`dashboard.calender.months.${currentDate.getMonth() + 1}`)}
                                </div>
                                <div className="text-brand-secondary text-lg font-bold">
                                    {currentDate.getDate()}
                                </div>
                            </div>
                            <div>
                                <h2 className="flex flex-row gap-2">
                                    <div className="text-xl font-semibold">
                                        {t(`dashboard.calender.monthsFullName.${currentDate.getMonth() + 1}`)}
                                        {' '}
                                        {currentDate.getFullYear()}
                                    </div>
                                    <div className="text-xs border-[#D5D7DA]">
                                        <span className="inline-block px-2 py-0.5 rounded-full border border-[#D5D7DA] text-xs font-medium align-middle mt-1">
                                            {t('dashboard.calender.week')}
                                            {' '}
                                            {currentWeek}
                                        </span>
                                    </div>
                                </h2>
                                <p className="mt-1 text-text-primary/70 text-base">
                                    {`${t(`dashboard.calender.months.${currentDate.getMonth() + 1}`)} 1, ${currentDate.getFullYear()} - ${t(`dashboard.calender.months.${currentDate.getMonth() + 1}`)} ${daysInMonth}, ${currentDate.getFullYear()} `}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-4">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={handlePrevious}
                            aria-label="Previous"
                        >
                            <ChevronLeftIcon size={16} aria-hidden="true" />
                        </Button>
                        <div className="flex items-center gap-2">
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline" className="gap-1.5 max-[479px]:h-8">
                                        <span>
                                            <span className="min-[480px]:hidden" aria-hidden="true">
                                                {view === 'day' && (<>{t('dashboard.planning.day')}</>)}
                                                {view === 'week' && (<>{t('dashboard.planning.week')}</>)}
                                                {view === 'month' && (<>{t('dashboard.planning.month')}</>)}
                                                {view === 'agenda' && (<>{t('dashboard.planning.agenda')}</>)}
                                                {!view && (<>{t('dashboard.planning.month')}</>)}
                                            </span>
                                            <span className="max-[479px]:sr-only">
                                                {view === 'day' && (<>{t('dashboard.planning.day')}</>)}
                                                {view === 'week' && (<>{t('dashboard.planning.week')}</>)}
                                                {view === 'month' && (<>{t('dashboard.planning.month')}</>)}
                                                {view === 'agenda' && (<>{t('dashboard.planning.agenda')}</>)}
                                                {!view && (<>{t('dashboard.planning.month')}</>)}
                                            </span>
                                        </span>
                                        <ChevronDownIcon
                                            className="-me-1 opacity-60"
                                            size={16}
                                            aria-hidden="true"
                                        />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="min-w-32">
                                    <DropdownMenuItem onClick={() => setView('day')}>
                                        {t('dashboard.planning.day')}
                                        {' '}
                                        <DropdownMenuShortcut>D</DropdownMenuShortcut>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => setView('week')}>
                                        {t('dashboard.planning.week')}
                                        {' '}
                                        <DropdownMenuShortcut>W</DropdownMenuShortcut>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => setView('month')}>
                                        {t('dashboard.planning.month')}
                                        {' '}
                                        <DropdownMenuShortcut>M</DropdownMenuShortcut>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                        <div className="flex items-center sm:gap-2">
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={handleNext}
                                aria-label="Next"
                            >
                                <ChevronRightIcon size={16} aria-hidden="true" />
                            </Button>
                        </div>
                    </div>
                </div>

                <div className="flex flex-1 flex-col">
                    {view === 'month' && (
                        <MonthView
                            currentDate={currentDate}
                            events={events ?? []}
                            onEventSelect={handleEventSelect}
                            onEventCreate={handleEventCreate}
                            selectedEventId={selectedEvent?.id ?? null}
                            onBackgroundClick={handleBackgroundClick}
                        />
                    )}
                    {view === 'week' && (
                        <WeekView
                            currentDate={currentDate}
                            events={events ?? []}
                            onEventSelect={handleEventSelect}
                            onEventCreate={handleEventCreate}
                            selectedEventId={selectedEvent?.id ?? null}
                            onBackgroundClick={handleBackgroundClick}
                        />
                    )}
                    {view === 'day' && (
                        <DayView
                            currentDate={currentDate}
                            events={events ?? []}
                            onEventSelect={handleEventSelect}
                            onEventCreate={handleEventCreate}
                            selectedEventId={selectedEvent?.id ?? null}
                            onBackgroundClick={handleBackgroundClick}
                        />
                    )}
                    {view === 'agenda' && (
                        <AgendaView
                            currentDate={currentDate}
                            events={events ?? []}
                            onEventSelect={handleEventSelect}
                        />
                    )}
                </div>
            </CalendarDndProvider>
        </div>
    );
}
